/**
 * music21j -- Javascript reimplementation of Core music21p features.
 * music21/tinyNotation -- TinyNotation implementation
 *
 * Copyright (c) 2013-24, Michael Scott Asato Cuthbert
 * Based on music21 (=music21p), Copyright (c) 2006-24, Michael Scott Asato Cuthbert
 *
 * TinyNotation module
 */
import * as chord from './chord';
import * as clef from './clef';
import * as duration from './duration';
import * as pitch from './pitch';
import * as note from './note';
import * as meter from './meter';
import * as stream from './stream';
import * as tie from './tie';

/**
 * Regular expressions object
 */
export const regularExpressions: { [k: string]: RegExp } = {
    REST: /r/,
    OCTAVE2: /([A-G])[A-G]+/,
    OCTAVE3: /([A-G])/,
    OCTAVE5: /([a-g])('+)/,
    OCTAVE4: /([a-g])/,
    EDSHARP: /\((#+)\)/,
    EDFLAT: /\((-+)\)/,
    EDNAT: /\(n\)/,
    SHARP: /^[A-Ga-g]+'*(#+)/,  // simple notation finds
    FLAT: /^[A-Ga-g]+'*(-+)/,  //   double accidentals too
    NAT: /^[A-Ga-g]+'*n/,  // explicit naturals
    TYPE: /(\d+)/,
    TIE: /.~/,  // not preceding ties
    PRECTIE: /~/,  // front ties   // TODO: remove these...
    ID_EL: /=([A-Za-z0-9]*)/,
    LYRIC: /_(.*)/,
    DOT: /\.+/,
    TIMESIG: /(\d+)\/(\d+)/,

    PARTBREAK: /partBreak/,  // nonstandard...fix later...

    CHORD: /chord{/,
    TRIP: /trip{/,
    QUAD: /quad{/,
    ENDBRAC: /}($|_)/,
};

/**
 * **Function, not class**.
 *
 * Converts a TinyNotation String into a music21 Stream
 *
 * See music21p for examples of what can go into tinyNotation. It's an
 * adaptation of Lilypond format, by design Extremely simple!
 *
 * * textIn - a valid tinyNotation string
 *
 * * returns {music21.stream.Part|music21.stream.Score} - a Part object or Score (if multiple parts)
 *
 * @example
 * var t = "3/4 c4 B8 c d4 e2.";
 * var p = music21.tinyNotation.TinyNotation(t);
 * p.duration.quarterLength;
 * // 6.0
 */
export function TinyNotation(textIn: string): stream.Part|stream.Score {
    textIn = textIn.trim();
    // compatibility with "tinyNotation: " in m21p
    if (textIn.slice(0, 14).toLowerCase() === 'tinynotation: ') {
        textIn = textIn.slice(14);
    }

    // whitespace that is not non-breaking space.
    const tokens: string[] = textIn.split(/[ \t\r\n]+/);

    let optionalScore: stream.Score;

    let p = new stream.Part();
    let m = new stream.Measure();
    m.number = 1;
    let currentTSBarDuration = 4.0;
    let lastDurationQL = 1.0;
    const storedDict = {
        lastNoteTied: false,
        inTrip: false,
        inQuad: false,
        inChord: false,
        endStateAfterNote: false,
    };
    let chordObj = null;
    const tnre = regularExpressions; // for ease in writing
    let measureNumber = 1;
    for (let i = 0; i < tokens.length; i++) {
        // check at first so that a full measure but not over full
        // gets returned as a stream.Measure object.
        if ((m.duration.quarterLength >= currentTSBarDuration)
            || (Math.abs(m.duration.quarterLength - currentTSBarDuration) < 0.0001)) {
            p.append(m);
            measureNumber += 1;
            m = new stream.Measure();
            m.number = measureNumber;
        }

        let token = tokens[i];
        let noteObj: note.GeneralNote|note.Note|note.Rest;
        let lyric: string;
        if (tnre.PARTBREAK.exec(token)) {
            if (m.length > 0) {
                p.append(m);
                m = new stream.Measure();
            }
            if (!optionalScore) {
                optionalScore = new stream.Score();
            }
            optionalScore.insert(0, p);
            p = new stream.Part();

            storedDict.lastNoteTied = false;
            storedDict.inTrip = false;
            storedDict.inQuad = false;
            storedDict.endStateAfterNote = false;

            continue;
        }

        if (tnre.TRIP.exec(token)) {
            token = token.slice(5); // cut...
            storedDict.inTrip = true;
        }
        if (tnre.QUAD.exec(token)) {
            token = token.slice(5); // cut...
            storedDict.inQuad = true;
        }
        if (tnre.CHORD.exec(token)) {
            token = token.slice(6); // cut...
            storedDict.inChord = true;
        }
        if (tnre.ENDBRAC.exec(token)) {
            if (chordObj && tnre.LYRIC.exec(token)) {
                let chordLyric: string;
                [token, chordLyric] = token.split('_');
                chordObj.lyric = chordLyric;
            }
            token = token.slice(0, -1); // cut...
            storedDict.endStateAfterNote = true;
        }

        // Modifiers
        if (tnre.LYRIC.exec(token)) {
            [token, lyric] = token.split('_');
        }

        if (tnre.TIMESIG.exec(token)) {
            const MATCH = tnre.TIMESIG.exec(token);
            const ts = new meter.TimeSignature();
            ts.numerator = parseInt(MATCH[1]);
            ts.denominator = parseInt(MATCH[2]);
            m.timeSignature = ts;
            currentTSBarDuration = ts.barDuration.quarterLength;
            // console.log(currentTSBarDuration);
            continue;
        } else if (tnre.REST.exec(token)) {
            noteObj = new note.Rest(lastDurationQL);
        } else if (tnre.OCTAVE2.exec(token)) {
            const MATCH = tnre.OCTAVE2.exec(token);
            const n = new note.Note(MATCH[1], lastDurationQL);
            n.pitch.octave = 4 - MATCH[0].length;
            noteObj = n;
        } else if (tnre.OCTAVE3.exec(token)) {
            const MATCH = tnre.OCTAVE3.exec(token);
            const n = new note.Note(MATCH[1], lastDurationQL);
            n.pitch.octave = 3;
            noteObj = n;
        } else if (tnre.OCTAVE5.exec(token)) {
            // must match octave 5 before 4
            const MATCH = tnre.OCTAVE5.exec(token);
            const n = new note.Note(MATCH[1].toUpperCase(), lastDurationQL);
            n.pitch.octave = 3 + MATCH[0].length;
            noteObj = n;
        } else if (tnre.OCTAVE4.exec(token)) {
            const MATCH = tnre.OCTAVE4.exec(token);
            const n = new note.Note(MATCH[1].toUpperCase(), lastDurationQL);
            n.pitch.octave = 4;
            noteObj = n;
        }

        if (noteObj === undefined) {
            continue;
        }

        if (lyric !== undefined) {
            noteObj.lyric = lyric;
        }

        if (tnre.TIE.exec(token)) {
            noteObj.tie = new tie.Tie('start');
            if (storedDict.lastNoteTied) {
                noteObj.tie.type = 'continue';
            }
            storedDict.lastNoteTied = true;
        } else if (storedDict.lastNoteTied) {
            noteObj.tie = new tie.Tie('stop');
            storedDict.lastNoteTied = false;
        }
        if (tnre.SHARP.exec(token) && noteObj instanceof note.Note) {
            const MATCH = tnre.SHARP.exec(token); // sharp
            noteObj.pitch.accidental = new pitch.Accidental(MATCH[1].length);
        } else if (tnre.FLAT.exec(token) && noteObj instanceof note.Note) {
            const MATCH = tnre.FLAT.exec(token); // sharp
            noteObj.pitch.accidental = new pitch.Accidental(-1 * MATCH[1].length);
        } else if (tnre.NAT.exec(token) && noteObj instanceof note.Note) {
            noteObj.pitch.accidental = new pitch.Accidental('natural');
            noteObj.pitch.accidental.displayType = 'always';
        }
        let MATCH = tnre.TYPE.exec(token);
        if (MATCH) {
            const durationType = parseInt(MATCH[0]);
            noteObj.duration.quarterLength = 4.0 / durationType;
        }
        MATCH = tnre.DOT.exec(token);
        if (MATCH) {
            const numDots = MATCH[0].length;
            const multiplier = 1 + (1 - 0.5 ** numDots);
            noteObj.duration.quarterLength *= multiplier;
        }
        lastDurationQL = noteObj.duration.quarterLength;
        // do before appending tuplets

        if (storedDict.inTrip) {
            // console.log(noteObj.duration.quarterLength);
            noteObj.duration.appendTuplet(
                new duration.Tuplet(3, 2, noteObj.duration.quarterLength)
            );
        }
        if (storedDict.inQuad) {
            noteObj.duration.appendTuplet(
                new duration.Tuplet(4, 3, noteObj.duration.quarterLength)
            );
        }
        if (storedDict.inChord && noteObj instanceof note.Note) {
            if (chordObj) {
                chordObj.add(noteObj);
            } else {
                chordObj = new chord.Chord([noteObj]);
            }
        }
        if (storedDict.endStateAfterNote) {
            storedDict.inTrip = false;
            storedDict.inQuad = false;
            storedDict.inChord = false;
            storedDict.endStateAfterNote = false;

            if (chordObj) {
                m.append(chordObj);
                chordObj = null;
                continue;
            }
        }
        if (!storedDict.inChord) {
            m.append(noteObj);
        }
    }

    if (m.length > 0) {
        p.append(m);
    }

    let returnObject: stream.Part|stream.Score;

    if (optionalScore !== undefined) {
        if (p.length > 0) {
            optionalScore.insert(0, p);
        }
        for (let i = 0; i < optionalScore.parts.length; i++) {
            const innerPart = optionalScore.parts.get(i);
            const innerPartClef = clef.bestClef(innerPart);
            const innerMeasure = innerPart.getElementsByClass('Measure').get(0) as stream.Measure;
            if (innerMeasure !== undefined) {
                innerMeasure.insert(0, innerPartClef);
            }
        }
        returnObject = optionalScore;
    } else {
        const bestClef = clef.bestClef(p);
        (p.getElementsByClass('Measure').get(0) as stream.Measure).insert(0, bestClef);
        returnObject = p;
    }
    return returnObject;
}

// render notation divs in HTML
/**
 * Render all the TinyNotation classes in the DOM as notation
 *
 * Called automatically when music21 is loaded.  TODO -- stop that!
 *
 * @param {string} [classTypes='.music21.tinyNotation'] - a JQuery selector to find elements to replace.
 * @param {HTMLElement|jQuery} [selector]
 */
export function renderNotationDivs(
    classTypes: string = '.music21.tinyNotation',
    selector: HTMLElement|string = undefined,
): void {
    let allRender: NodeList;

    if (selector === undefined) {
        allRender = document.querySelectorAll(classTypes);
    } else if (typeof selector === 'string') {
        allRender = document.querySelectorAll(selector + ' ' + classTypes);
    } else {
        allRender = selector.querySelectorAll(classTypes);
    }

    for (let i = 0; i < allRender.length; i++) {
        const thisTN = allRender[i] as HTMLElement;
        let thisTNContents: string;
        if (thisTN.getAttribute('tinynotationcontents')) {
            thisTNContents = thisTN.getAttribute('tinynotationcontents');
        } else if (thisTN.textContent) {
            thisTNContents = thisTN.textContent;
            thisTNContents = thisTNContents.replace(/s+/g, ' '); // no line-breaks, etc.
        }

        if (thisTNContents) {
            thisTNContents = thisTNContents.trim(); // remove leading, trailing whitespace
        }
        if (thisTNContents) {
            const st = TinyNotation(thisTNContents);
            if (thisTN.classList.contains('noPlayback')) {
                st.renderOptions.events.click = undefined;
            }
            const newSVG = st.createDOM();

            thisTN.setAttribute('tinynotationcontents', thisTNContents);
            thisTN.replaceChildren();
            // thisTN.data('stream', st);
            thisTN.appendChild(newSVG);
            // console.log(thisTNContents);
        }
    }
}
